import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["children"];
var _dec, _class, _ModalHeader;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Children, Component } from 'react';
import { matchComponentTypes, passthroughProps } from '@instructure/ui-react-utils';
import { withStyle } from '@instructure/emotion';
import { CloseButton } from '@instructure/ui-buttons';
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
import ModalContext from "../ModalContext.js";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
parent: Modal
id: Modal.Header
---
**/
let ModalHeader = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_ModalHeader = class ModalHeader extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    /**
     * Gets all text in a DOM subtree, text under <button> nodes is excluded
     */
    this.getTextExcludingButtons = root => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          var _node$parentElement;
          return (_node$parentElement = node.parentElement) !== null && _node$parentElement !== void 0 && _node$parentElement.closest('button') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        }
      });
      let text = '';
      let current;
      while (current = walker.nextNode()) {
        text += current.nodeValue;
      }
      return text;
    };
    this.handleRef = el => {
      this.ref = el;
      if (el) {
        var _this$context$setBody, _this$context;
        const txt = this.getTextExcludingButtons(el);
        (_this$context$setBody = (_this$context = this.context).setBodyScrollAriaLabel) === null || _this$context$setBody === void 0 ? void 0 : _this$context$setBody.call(_this$context, txt);
      }
    };
    this.makeStyleProps = () => {
      return {
        withCloseButton: this.usesCloseButton
      };
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStyleProps());
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStyleProps());
  }
  get usesCloseButton() {
    let hasCloseButton = false;
    Children.forEach(this.props.children, child => {
      if (child && matchComponentTypes(child, [CloseButton])) {
        hasCloseButton = true;
      }
    });
    return hasCloseButton;
  }
  render() {
    var _this$props$styles;
    const _this$props3 = this.props,
      children = _this$props3.children,
      rest = _objectWithoutProperties(_this$props3, _excluded);
    return _jsx("div", {
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.modalHeader,
      ...passthroughProps(rest),
      ref: this.handleRef,
      "data-cid": "ModalHeader",
      children: children
    });
  }
}, _ModalHeader.displayName = "ModalHeader", _ModalHeader.componentId = 'Modal.Header', _ModalHeader.allowedProps = allowedProps, _ModalHeader.defaultProps = {
  variant: 'default',
  spacing: 'default'
}, _ModalHeader.contextType = ModalContext, _ModalHeader)) || _class);
export default ModalHeader;
export { ModalHeader };